<script setup>
import { ref, computed, watch, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore } from 'vuex';
import { useAlert } from 'dashboard/composables';
import { uploadFile } from 'dashboard/helper/uploadHelper';
import { checkFileSizeLimit } from 'shared/helpers/FileHelper';

import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import TextArea from 'dashboard/components-next/textarea/TextArea.vue';
import ComboBox from 'dashboard/components-next/combobox/ComboBox.vue';
import Switch from 'dashboard/components-next/switch/Switch.vue';

const props = defineProps({
  campaign: {
    type: Object,
    default: null,
  },
  isEditing: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(['close']);

const { t } = useI18n();
const store = useStore();

const dialogRef = ref(null);
const isSubmitting = ref(false);
const isUploading = ref(false);
const fileInputRef = ref(null);
const isMessageExpanded = ref(false);

const MAXIMUM_FILE_UPLOAD_SIZE = 10; // in MB

const form = ref({
  title: '',
  message: '',
  image_url: '',
  inbox_id: null,
  contact_list_id: null,
  start_date: '',
  end_date: '',
  start_time: '08:00',
  end_time: '20:00',
  enabled: false,
  send_interval: 5,
  weekdays: [],
  recurring_weekly: false,
});

// Days of week configuration (0 = Sunday, 1 = Monday, ..., 6 = Saturday)
const weekdayOptions = computed(() => [
  { value: 0, label: t('WHATSAPP_BROADCAST.FORM.WEEKDAYS.SUNDAY') },
  { value: 1, label: t('WHATSAPP_BROADCAST.FORM.WEEKDAYS.MONDAY') },
  { value: 2, label: t('WHATSAPP_BROADCAST.FORM.WEEKDAYS.TUESDAY') },
  { value: 3, label: t('WHATSAPP_BROADCAST.FORM.WEEKDAYS.WEDNESDAY') },
  { value: 4, label: t('WHATSAPP_BROADCAST.FORM.WEEKDAYS.THURSDAY') },
  { value: 5, label: t('WHATSAPP_BROADCAST.FORM.WEEKDAYS.FRIDAY') },
  { value: 6, label: t('WHATSAPP_BROADCAST.FORM.WEEKDAYS.SATURDAY') },
]);

const toggleWeekday = (dayValue) => {
  const index = form.value.weekdays.indexOf(dayValue);
  if (index === -1) {
    form.value.weekdays.push(dayValue);
  } else {
    form.value.weekdays.splice(index, 1);
  }
};

const isWeekdaySelected = (dayValue) => {
  return form.value.weekdays.includes(dayValue);
};

const inboxes = computed(() => {
  const allInboxes = store.getters['inboxes/getInboxes'];
  // Include both WhatsApp and API inboxes (API inboxes can be used for WhatsApp via API)
  return allInboxes.filter(
    inbox =>
      inbox.channel_type === 'Channel::Whatsapp' ||
      inbox.channel_type === 'Channel::Api'
  );
});

const contactLists = computed(() => store.getters['contactLists/getContactLists']);

const inboxOptions = computed(() =>
  inboxes.value.map(inbox => ({
    value: inbox.id,
    label: inbox.name,
  }))
);

const contactListOptions = computed(() =>
  contactLists.value.map(list => ({
    value: list.id,
    label: `${list.name} (${list.contacts_count} contatos)`,
  }))
);

const dialogTitle = computed(() =>
  props.isEditing
    ? t('WHATSAPP_BROADCAST.EDIT.TITLE')
    : t('WHATSAPP_BROADCAST.CREATE.TITLE')
);

const isFormValid = computed(() => {
  return form.value.title && form.value.message && form.value.inbox_id;
});

const messageTextAreaHeight = computed(() => {
  return isMessageExpanded.value ? '20rem' : '6rem';
});

watch(
  () => props.campaign,
  newCampaign => {
    if (newCampaign) {
      form.value = {
        title: newCampaign.title || '',
        message: newCampaign.message || '',
        image_url: newCampaign.image_url || '',
        inbox_id: newCampaign.inbox_id || null,
        contact_list_id: newCampaign.contact_list_id || null,
        start_date: newCampaign.start_date || '',
        end_date: newCampaign.end_date || '',
        start_time: newCampaign.start_time || '08:00',
        end_time: newCampaign.end_time || '20:00',
        enabled: newCampaign.enabled || false,
        send_interval: newCampaign.send_interval || 5,
        weekdays: newCampaign.weekdays || [],
        recurring_weekly: newCampaign.recurring_weekly || false,
      };
    }
  },
  { immediate: true }
);

onMounted(() => {
  dialogRef.value?.open();
  // Fetch contact lists when dialog opens
  store.dispatch('contactLists/get');
});

const handleClose = () => {
  dialogRef.value?.close();
  emit('close');
};

const toggleMessageExpand = () => {
  isMessageExpanded.value = !isMessageExpanded.value;
};

const openFileSelector = () => {
  fileInputRef.value?.click();
};

const handleFileUpload = async event => {
  const file = event.target.files[0];
  if (!file) return;

  // Validate file size
  if (!checkFileSizeLimit(file, MAXIMUM_FILE_UPLOAD_SIZE)) {
    useAlert(t('WHATSAPP_BROADCAST.FORM.IMAGE.SIZE_ERROR', { size: MAXIMUM_FILE_UPLOAD_SIZE }));
    return;
  }

  // Validate file type
  const validTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];
  if (!validTypes.includes(file.type)) {
    useAlert(t('WHATSAPP_BROADCAST.FORM.IMAGE.TYPE_ERROR'));
    return;
  }

  isUploading.value = true;

  try {
    const { fileUrl } = await uploadFile(file);
    form.value.image_url = fileUrl;
    useAlert(t('WHATSAPP_BROADCAST.FORM.IMAGE.UPLOAD_SUCCESS'));
  } catch (error) {
    useAlert(t('WHATSAPP_BROADCAST.FORM.IMAGE.UPLOAD_ERROR'));
  } finally {
    isUploading.value = false;
    // Reset file input
    if (fileInputRef.value) {
      fileInputRef.value.value = '';
    }
  }
};

const removeImage = () => {
  form.value.image_url = '';
};

const handleSubmit = async () => {
  if (!isFormValid.value) return;

  isSubmitting.value = true;

  try {
    if (props.isEditing) {
      await store.dispatch('whatsappCampaigns/update', {
        id: props.campaign.id,
        ...form.value,
      });
      useAlert(t('WHATSAPP_BROADCAST.API.UPDATE_SUCCESS'));
    } else {
      await store.dispatch('whatsappCampaigns/create', form.value);
      useAlert(t('WHATSAPP_BROADCAST.API.CREATE_SUCCESS'));
    }
    handleClose();
  } catch (error) {
    useAlert(t('WHATSAPP_BROADCAST.API.ERROR'));
  } finally {
    isSubmitting.value = false;
  }
};
</script>

<template>
  <Dialog
    ref="dialogRef"
    :title="dialogTitle"
    width="2xl"
    overflow-y-auto
    @close="handleClose"
  >
    <form class="space-y-4 max-h-[70vh] overflow-y-auto pr-2" @submit.prevent="handleSubmit">
      <div class="grid grid-cols-2 gap-4">
        <!-- Title -->
        <Input
          v-model="form.title"
          :label="t('WHATSAPP_BROADCAST.FORM.TITLE.LABEL')"
          :placeholder="t('WHATSAPP_BROADCAST.FORM.TITLE.PLACEHOLDER')"
        />

        <!-- Inbox -->
        <div class="flex flex-col gap-1">
          <label class="mb-0.5 text-sm font-medium text-n-slate-12">
            {{ t('WHATSAPP_BROADCAST.FORM.INBOX.LABEL') }}
          </label>
          <ComboBox
            v-model="form.inbox_id"
            :options="inboxOptions"
            :placeholder="t('WHATSAPP_BROADCAST.FORM.INBOX.PLACEHOLDER')"
            class="[&>div>button]:bg-n-alpha-black2"
          />
        </div>
      </div>

      <!-- Message with expand button -->
      <div class="relative">
        <div class="flex items-center justify-between mb-1">
          <label class="text-sm font-medium text-n-slate-12">
            {{ t('WHATSAPP_BROADCAST.FORM.MESSAGE.LABEL') }}
          </label>
          <button
            type="button"
            class="flex items-center gap-1 text-xs text-n-slate-10 hover:text-n-slate-12 transition-colors"
            @click.stop.prevent="toggleMessageExpand"
          >
            <span :class="isMessageExpanded ? 'i-lucide-minimize-2' : 'i-lucide-maximize-2'" />
            {{ isMessageExpanded ? t('WHATSAPP_BROADCAST.FORM.MESSAGE.COLLAPSE') : t('WHATSAPP_BROADCAST.FORM.MESSAGE.EXPAND') }}
          </button>
        </div>
        <TextArea
          v-model="form.message"
          :placeholder="t('WHATSAPP_BROADCAST.FORM.MESSAGE.PLACEHOLDER')"
          :min-height="messageTextAreaHeight"
          :max-height="isMessageExpanded ? '30rem' : '12rem'"
          auto-height
        />
        <p class="mt-1 text-xs text-n-slate-10">
          {{ t('WHATSAPP_BROADCAST.FORM.MESSAGE.HELP') }}
        </p>
      </div>

      <div class="grid grid-cols-2 gap-4">
        <!-- Contact List -->
        <div class="flex flex-col gap-1">
          <label class="mb-0.5 text-sm font-medium text-n-slate-12">
            {{ t('WHATSAPP_BROADCAST.FORM.CONTACT_LIST.LABEL') }}
          </label>
          <ComboBox
            v-model="form.contact_list_id"
            :options="contactListOptions"
            :placeholder="t('WHATSAPP_BROADCAST.FORM.CONTACT_LIST.PLACEHOLDER')"
            class="[&>div>button]:bg-n-alpha-black2"
          />
        </div>

        <!-- Image Upload -->
        <div class="flex flex-col gap-1">
          <label class="mb-0.5 text-sm font-medium text-n-slate-12">
            {{ t('WHATSAPP_BROADCAST.FORM.IMAGE.LABEL') }}
          </label>

          <!-- Hidden file input -->
          <input
            ref="fileInputRef"
            type="file"
            accept="image/jpeg,image/png,image/gif,image/webp"
            class="hidden"
            @change="handleFileUpload"
          />

          <!-- Image preview or upload button -->
          <div v-if="form.image_url" class="flex items-center gap-2">
            <div class="relative w-16 h-16 rounded-lg overflow-hidden bg-n-alpha-2 flex-shrink-0">
              <img
                :src="form.image_url"
                alt="Preview"
                class="w-full h-full object-cover"
              />
            </div>
            <div class="flex flex-col gap-1 flex-1 min-w-0">
              <p class="text-xs text-n-slate-11 truncate">{{ form.image_url }}</p>
              <div class="flex gap-2">
                <Button
                  type="button"
                  size="xs"
                  variant="faded"
                  color="slate"
                  icon="i-lucide-refresh-cw"
                  :label="t('WHATSAPP_BROADCAST.FORM.IMAGE.CHANGE')"
                  :is-loading="isUploading"
                  @click.stop.prevent="openFileSelector"
                />
                <Button
                  type="button"
                  size="xs"
                  variant="faded"
                  color="ruby"
                  icon="i-lucide-trash-2"
                  @click.stop.prevent="removeImage"
                />
              </div>
            </div>
          </div>
          <div v-else>
            <Button
              type="button"
              size="sm"
              variant="faded"
              color="slate"
              icon="i-lucide-upload"
              :label="t('WHATSAPP_BROADCAST.FORM.IMAGE.UPLOAD')"
              :is-loading="isUploading"
              class="w-full"
              @click.stop.prevent="openFileSelector"
            />
            <p class="mt-1 text-xs text-n-slate-10">
              {{ t('WHATSAPP_BROADCAST.FORM.IMAGE.HELP') }}
            </p>
          </div>
        </div>
      </div>

      <div class="grid grid-cols-2 gap-4">
        <!-- Start Date -->
        <Input
          v-model="form.start_date"
          :label="t('WHATSAPP_BROADCAST.FORM.START_DATE.LABEL')"
          type="date"
        />

        <!-- End Date -->
        <Input
          v-model="form.end_date"
          :label="t('WHATSAPP_BROADCAST.FORM.END_DATE.LABEL')"
          type="date"
        />
      </div>

      <div class="grid grid-cols-2 gap-4">
        <!-- Start Time -->
        <Input
          v-model="form.start_time"
          :label="t('WHATSAPP_BROADCAST.FORM.START_TIME.LABEL')"
          type="time"
        />

        <!-- End Time -->
        <Input
          v-model="form.end_time"
          :label="t('WHATSAPP_BROADCAST.FORM.END_TIME.LABEL')"
          type="time"
        />
      </div>

      <!-- Weekdays Selection -->
      <div class="flex flex-col gap-2">
        <label class="text-sm font-medium text-n-slate-12">
          {{ t('WHATSAPP_BROADCAST.FORM.WEEKDAYS.LABEL') }}
        </label>
        <div class="flex flex-wrap gap-2">
          <button
            v-for="day in weekdayOptions"
            :key="day.value"
            type="button"
            class="px-3 py-1.5 text-sm rounded-lg border transition-colors"
            :class="isWeekdaySelected(day.value)
              ? 'bg-n-brand text-white border-n-brand'
              : 'bg-n-alpha-2 text-n-slate-11 border-n-weak hover:bg-n-alpha-3'"
            @click="toggleWeekday(day.value)"
          >
            {{ day.label }}
          </button>
        </div>
        <p class="text-xs text-n-slate-10">
          {{ t('WHATSAPP_BROADCAST.FORM.WEEKDAYS.HELP') }}
        </p>
      </div>

      <!-- Send Interval -->
      <div class="flex flex-col gap-1">
        <Input
          v-model.number="form.send_interval"
          :label="t('WHATSAPP_BROADCAST.FORM.SEND_INTERVAL.LABEL')"
          type="number"
          min="1"
          max="300"
        />
        <p class="text-xs text-n-slate-10">
          {{ t('WHATSAPP_BROADCAST.FORM.SEND_INTERVAL.HELP') }}
        </p>
      </div>

      <!-- Enabled -->
      <div class="flex items-center gap-3">
        <Switch v-model="form.enabled" />
        <span class="text-sm text-n-slate-11">
          {{ t('WHATSAPP_BROADCAST.FORM.ENABLED.LABEL') }}
        </span>
      </div>

      <!-- Recurring Weekly (only shown when weekdays are selected) -->
      <div v-if="form.weekdays.length > 0" class="flex flex-col gap-2 p-3 bg-n-alpha-2 rounded-lg">
        <div class="flex items-center gap-3">
          <Switch v-model="form.recurring_weekly" />
          <span class="text-sm text-n-slate-11">
            {{ t('WHATSAPP_BROADCAST.FORM.RECURRING_WEEKLY.LABEL') }}
          </span>
        </div>
        <p class="text-xs text-n-slate-10">
          {{ t('WHATSAPP_BROADCAST.FORM.RECURRING_WEEKLY.HELP') }}
        </p>
      </div>
    </form>

    <template #footer>
      <div class="flex justify-end gap-2">
        <Button
          variant="faded"
          color="slate"
          type="button"
          :label="t('WHATSAPP_BROADCAST.FORM.CANCEL')"
          class="bg-n-alpha-2 text-n-blue-text hover:bg-n-alpha-3"
          @click="handleClose"
        />
        <Button
          :label="isEditing ? t('WHATSAPP_BROADCAST.FORM.UPDATE') : t('WHATSAPP_BROADCAST.FORM.CREATE')"
          :is-loading="isSubmitting"
          :disabled="!isFormValid || isSubmitting"
          @click="handleSubmit"
        />
      </div>
    </template>
  </Dialog>
</template>
